import { Box, Popover } from '@mui/material';
import { useIsMobile } from '@uplift/hooks';
import { Button, ButtonProps, ModalFullPage, ModalFullPageActions, ModalFullPageContent, ModalFullPageTitle, ScrollableView } from '@uplift/ui-v2';
import React, { RefObject, useContext, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { FilterCountChip } from './filter-count-chip';

type FiltersMenuContextType = {
  anchor: RefObject<HTMLButtonElement> | null;
  open: boolean;
  onClose: (reason: 'submit' | 'cancel') => void;
  onOpen: () => void;
};
export const FiltersMenuContext = React.createContext<FiltersMenuContextType>({
  anchor: null,
  open: false,
  onClose: () => null,
  onOpen: () => null,
});

type FiltersMenuContextProviderProps = {
  onCloseCallback?: (reason: 'submit' | 'cancel') => void;
};

export const FiltersMenuProvider: React.FC<FiltersMenuContextProviderProps> = props => {
  const anchor = useRef<HTMLButtonElement>(null);
  const [ open, setOpen ] = useState<boolean | null>(null);

  const onClose = (reason: 'submit' | 'cancel') => {
    setOpen(false);
    props.onCloseCallback?.(reason);
  };

  const onOpen = () => {
    setOpen(true);
  };

  return (
    <FiltersMenuContext.Provider value={{ open, onClose, onOpen, anchor }}>
      {props.children}
    </FiltersMenuContext.Provider>
  );
};

export const FiltersMenuButton: React.FC<ButtonProps & { isActive?: boolean; chipCount?: number }> = ({ className, children, isActive = false, chipCount, ...props }) => {
  const { anchor, onOpen } = useContext(FiltersMenuContext);
  const { classes, cx } = useStyles();

  return (
    <Button
      className={cx(classes.filterButton, className)}
      data-active={isActive}
      onClick={onOpen}
      ref={anchor}
      endIcon={chipCount ? <FilterCountChip count={chipCount} /> : undefined}
      {...props}
    >
      <Box width="100%">
        {children}
      </Box>
    </Button>
  );
};

type FiltersMenuProps = {
  title: React.ReactNode;
  children: React.ReactNode;
  actions: React.ReactNode;
};

export const FiltersMenu: React.FC<FiltersMenuProps> = props => {
  const isMobile = useIsMobile('sm');
  const { classes } = useStyles();
  const { anchor, open, onClose } = useContext(FiltersMenuContext);

  if (isMobile) {
    return (
      <ModalFullPage closeIcon open={open} onClose={() => onClose('cancel')}>
        <ModalFullPageTitle>{props.title}</ModalFullPageTitle>
        <ModalFullPageContent dividers classes={{ root: classes.dialogContent }}>
          {props.children}
        </ModalFullPageContent>
        <ModalFullPageActions>
          {props.actions}
        </ModalFullPageActions>
      </ModalFullPage>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchor?.current}
      onClose={() => onClose('cancel')}
      classes={{ root: classes.popoverRoot, paper: classes.popoverPaper }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
    >
      <ScrollableView height="400px">
        {props.children}
      </ScrollableView>
      <Box borderTop="1px solid" borderColor="divider" p={1}>
        {props.actions}
      </Box>
    </Popover>
  );
};

const useStyles = makeStyles({ name: 'FiltersMenu' })(theme => ({
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    [theme.breakpoints.up('sm')]: {
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2),
    },
    backgroundColor: theme.palette.background.paper,
    zIndex: 2
  },
  actionsEnd: {
    justifyContent: 'flex-end'
  },
  popoverRoot: {
    top: '8px !important',
  },
  popoverPaper: {
    maxWidth: '400px',
    width: '100%',
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogContent: {
    padding: '0 !important',
  },
  dialogActions: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
  filterButton: {
    display: 'flex',
    justifyContent: 'stretch',
    background: 'transparent',
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    padding: theme.spacing(1, 1.5),
    textTransform: 'none',
    [theme.breakpoints.up('md')]: {
      minWidth: '170px',
      maxWidth: '170px',
    },
    height: '100%',
    '&[data-active="true"]': {
      borderColor: theme.palette.secondary.border,
      '& svg': {
        color: theme.palette.secondary.main,
      },
    },
    '&:disabled': {
      opacity: 0.5,
    }
  }
}));
